// Entry point (docs/platform/ClientShell.md): `/` creates a match (a random link), `/m/<id>` is a
// room (lobby, then the match). `?replay` is the offline playback used by the frame tests
// (client/tests/shots.mjs): the page waits for `window.__wmdReplay(snapshot, options)`.
import './style.css';
import { loadManifest, assetUrl } from './assets.ts';
import { Backdrop } from './render/backdrop.ts';
import { backdropThemes, loadArt, themeFrontendAudio } from './ui/feart.ts';
import { PROFILE, audio } from './game/audio.ts';
import { createRoom, type LobbyCmd, type LobbyState, type MatchTeam, type ServerMsg } from './net/protocol.ts';
import { MatchScreen } from './game/match.ts';
import type { TeamInfo } from './game/hud.ts';
import { loadIndex } from './render/meshes.ts';
import { Lobby } from './ui/lobby.ts';
import type { FromWorker, ToWorker } from './sim/messages.ts';
import { FRAME_STRIDE, type PageTrace, type WorkerTrace } from './sim/trace.ts';

const app = document.getElementById('app') ?? document.body;

/** Test hooks (window.__wmd; client/tests): the room's state as this client sees it. */
type TestHooks = {
  stats?: MatchScreen['stats']; status?: string; lobby?: LobbyState | null; match?: MatchScreen | null; errors: string[]; ready?: boolean;
  me?: number; teams?: MatchTeam[]; desyncs: number[]; released: number[]; over?: unknown;
  /** The lobby's landscape preview (ui/preview.ts). */
  preview?: Lobby['preview']['stats'];
};
const hooks: TestHooks = { errors: [], desyncs: [], released: [] };
Object.assign(window, { __wmd: hooks });
window.addEventListener('error', e => hooks.errors.push(`error: ${e.message}`));
window.addEventListener('unhandledrejection', e => hooks.errors.push(`unhandled: ${String(e.reason instanceof Error ? e.reason.stack ?? e.reason.message : e.reason)}`));

// The audio (game/audio.ts, docs/platform/UiFidelity.md §3): browsers start it on a gesture; its
// FMOD update runs every frame (the matches add their sim's events), so the front end's music
// and its fades run without a match too.
for (const ev of ['pointerdown', 'keydown']) window.addEventListener(ev, () => audio.unlock(), { capture: true });
setInterval(() => audio.tick(), 1000 / 60);

/** `ChangeBackgroundMusic` (Home's OnCreate, and back from a match): theme `name`'s front-end
 * music and ambience. */
function frontEndMusic(name: string | null): void {
  if (!name) return;
  void audio.load().then(() => themeFrontendAudio(name)).then(a => audio.frontEnd(a.music, a.ambience))
    .catch(e => console.warn('front-end music', e));
}

function home(): void {
  // The front end's look (ui/feart.ts): the animated backdrop of a random theme (wmd_frontend's
  // XOM_Background_01 shows one theme's FrontendResource), the logo, the multiplayer menu's tiles.
  const backdrop = new Backdrop();
  document.body.prepend(backdrop.el);
  app.innerHTML = `<main class="home">
    <h1 class="home-logo"><span>Worms W.M.D</span></h1>
    <section class="panel home-panel">
      <h2 class="panel-head">Online match</h2>
      <div class="home-tiles">
        <div class="home-tile">
          <div class="tile-art" style="background-image: var(--art-fe-match-hostgame-01)"></div>
          <p>Create a match and share its link; everyone with the link can join.</p>
          <button id="create" class="btn btn-big">Create match</button> <span id="create-error" class="error" role="alert"></span>
        </div>
        <form class="home-tile" id="join">
          <div class="tile-art" style="background-image: var(--art-fe-match-browsegame-01)"></div>
          <p class="or"><label for="link">Or open a link:</label></p>
          <div class="field-row"><input id="link" placeholder="https://…/m/…"> <button id="go" class="btn">Join</button></div>
        </form>
      </div>
    </section>
  </main>`;
  void (async () => {
    await loadManifest();
    void loadArt();
    await loadIndex();
    const themes = await backdropThemes();
    const theme = themes[Math.floor(Math.random() * themes.length)] ?? null;
    backdrop.setTheme(theme);
    backdrop.start();
    frontEndMusic(theme);
  })().catch(e => console.warn('home page art', e));
  document.getElementById('create')?.addEventListener('click', async () => {
    try {
      const r = await createRoom();
      localStorage.setItem(`wmd.hostkey.${r.id}`, r.host_key);
      location.assign(r.path);
    } catch (e) {
      const el = document.getElementById('create-error');
      if (el) el.textContent = e instanceof Error ? e.message : String(e);
    }
  });
  document.getElementById('join')?.addEventListener('submit', e => {
    e.preventDefault();
    const input = document.getElementById('link');
    const v = input instanceof HTMLInputElement ? input.value : '';
    const m = /\/m\/([0-9A-Za-z-]+)/.exec(v) ?? /^([0-9A-Za-z-]{16,})$/.exec(v.trim());
    if (m) location.assign(`/m/${m[1]}`);
  });
}

/** The HUD's view of the match teams: name, the slot's colour, ownership, the owning member's
 * lobby name, the room's wins. */
function teamInfos(teams: MatchTeam[], state: LobbyState | null): TeamInfo[] {
  return teams.map(t => {
    const player = t.owner.t === 'Human' ? t.owner.player : null;
    return {
      name: t.name, colour: state?.slots[t.slot]?.team.colour ?? t.slot, mine: player !== null && player === hooks.me,
      wins: t.wins ?? 0, user: player === null ? null : state?.members.find(m => m.id === player)?.name ?? null,
    };
  });
}

function startWorker(onMsg: (m: FromWorker) => void): (m: ToWorker) => void {
  const w = new Worker(new URL('./sim/worker.ts', import.meta.url), { type: 'module' });
  w.onmessage = (e: MessageEvent<FromWorker>) => onMsg(e.data);
  w.onerror = (e) => { hooks.errors.push(String(e.message)); console.error(e); };
  const post = (m: ToWorker): void => w.postMessage(m);
  post({ type: 'init', wasmUrl: '/simwasm.wasm', bundleUrl: assetUrl('bundle.bin') });
  return post;
}

function playerName(): string {
  let n = localStorage.getItem('wmd.name');
  if (!n) {
    n = `Player ${Math.floor(1000 + Math.random() * 9000)}`;
    localStorage.setItem('wmd.name', n);
  }
  return new URLSearchParams(location.search).get('name') ?? n;
}

async function room(id: string): Promise<void> {
  app.innerHTML = '<div class="status-line" id="status" role="status">Loading…</div>';
  const statusEl = document.getElementById('status');
  const setStatus = (t: string, error = false): void => {
    hooks.status = t;
    if (!statusEl) return;
    statusEl.textContent = t;
    statusEl.classList.toggle('error', error);
  };
  await Promise.all([loadManifest(), loadIndex()]);
  const lobbyHost = document.createElement('div');
  app.appendChild(lobbyHost);
  let match: MatchScreen | null = null;
  let teams: MatchTeam[] = [];
  let lobbyState: LobbyState | null = null;
  const lobby = new Lobby(lobbyHost, cmd => post({ type: 'lobby', cmd }));
  lobby.onChat = text => post({ type: 'send', msg: { t: 'Chat', text } });
  hooks.preview = lobby.preview.stats;
  Object.assign(window, { __wmdPreviewPicture: () => lobby.preview.pictureDataUrl() });
  lobby.render();
  lobby.backdrop.start();
  // The front end's fonts and sprites (ui/feart.ts); the forts' preview images need a render.
  void loadArt().then(() => { if (!match) lobby.render(); });
  const hostKey = new URLSearchParams(location.hash.slice(1)).get('host') ?? localStorage.getItem(`wmd.hostkey.${id}`);
  // The pacing trace (sim/trace.ts, client/tests/pacing.mjs): `?trace` turns it on before the
  // match boots (or restores), so the worker also records the match log. The page half lives here,
  // not in the match screen, so its records outlive the match (MatchOver drops the screen).
  const tracing = new URLSearchParams(location.search).has('trace');
  const pageTrace: PageTrace | null = tracing ? { frames: [], messages: [] } : null;
  let workerTrace: WorkerTrace[] = [];
  let traceWaiters: (() => void)[] = [];
  let matchLogWaiters: ((bytes: Uint8Array | null) => void)[] = [];
  let simBuild = '';
  let frontEndStarted = false;
  const startMatch = (): void => {
    if (match) return;
    // The lobby's OnDestroy: FadeOutAllType(kMainTrack).
    audio.leaveFrontEnd();
    lobbyHost.style.display = 'none';
    lobby.preview.stop();
    lobby.backdrop.stop();
    match = new MatchScreen(app, post, lobbyState?.settings.landscape.theme ?? null);
    match.trace = pageTrace;
    hooks.match = match;
    hooks.stats = match.stats;
    match.setStatus('Generating the landscape…');
    match.start();
  };
  const endMatch = (): void => {
    match?.stop();
    match = null;
    hooks.match = null;
    lobbyHost.style.display = '';
    lobby.backdrop.start();
    // Home_CameFromGame_Monitor: RefreshVolumes and ChangeBackgroundMusic.
    audio.resultsMusicEnd();
    audio.setSoundLevels(PROFILE.music, PROFILE.sfx, PROFILE.narration);
    frontEndMusic(lobbyState?.settings.landscape.theme ?? null);
  };
  const onServer = (m: ServerMsg): void => {
    switch (m.t) {
      case 'Welcome':
        localStorage.setItem(`wmd.token.${m.room}`, m.token);
        lobby.me = m.you;
        hooks.me = m.you;
        lobby.catalogue = m.catalogue;
        break;
      case 'Lobby':
        lobbyState = m;
        hooks.lobby = m;
        lobby.state = m;
        lobby.render();
        // The front end's music keeps the theme it started with (the original's lobbies change
        // only the preview's sky with the selected theme).
        if (!frontEndStarted && !match) { frontEndStarted = true; frontEndMusic(m.settings.landscape.theme); }
        if (match && lobbyState) match.setTeams(teamInfos(teams, m), true);
        break;
      case 'MatchStart':
        teams = m.teams;
        hooks.teams = m.teams;
        startMatch();
        match?.setTeams(teamInfos(m.teams, lobbyState), true);
        break;
      case 'MatchOver':
        hooks.over = m;
        match?.hud.banner(m.detail ? `Match over: ${m.detail}` : `Match over (${m.reason})`);
        setTimeout(endMatch, 4000);
        break;
      case 'Chat':
        lobby.addChat(`${lobbyState?.members.find(x => x.id === m.from)?.name ?? m.from}: ${m.text}`);
        break;
      case 'Desync':
        hooks.desyncs.push(m.tick);
        hooks.errors.push(`desync at ${m.tick}`);
        match?.hud.banner(m.resync ? 'Resynchronising…' : 'Out of sync with the server');
        break;
      case 'Error':
        hooks.errors.push(`${m.code}: ${m.message}`);
        setStatus(`${m.code}: ${m.message}`, true);
        break;
      case 'BarrierStatus':
        if (m.waiting.length === 0) hooks.released.push(m.tick);
        break;
      default: break;
    }
  };
  const post = startWorker(m => {
    switch (m.type) {
      case 'status': setStatus(m.text); match?.setStatus(m.text); break;
      case 'ready':
        hooks.ready = true;
        simBuild = m.simBuild;
        setStatus('');
        if (tracing) post({ type: 'trace', on: true });
        post({ type: 'connect', wsUrl: `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws/${id}`, name: playerName(), token: localStorage.getItem(`wmd.token.${id}`), hostKey });
        break;
      case 'server': onServer(m.msg); break;
      case 'match': if (!m.booting) match?.setStatus(''); break;
      case 'error': hooks.errors.push(m.text); setStatus(m.text, true); match?.setStatus(m.text); break;
      case 'trace':
        workerTrace.push(m.trace);
        for (const w of traceWaiters) w();
        traceWaiters = [];
        break;
      case 'matchLog':
        for (const w of matchLogWaiters) w(m.bytes);
        matchLogWaiters = [];
        break;
      default: match?.onWorker(m); break;
    }
  });
  /** The trace records since the last take: the page's and the worker's batches that arrived (the
   * worker posts its next batch now). */
  const takeTrace = (): { page: PageTrace | null; worker: WorkerTrace[] } => {
    const page = pageTrace ? { frames: pageTrace.frames, messages: pageTrace.messages } : null;
    if (pageTrace) { pageTrace.frames = []; pageTrace.messages = []; }
    const worker = workerTrace;
    workerTrace = [];
    post({ type: 'traceTake' });
    return { page, worker };
  };
  const takeMatchLog = (): Promise<Uint8Array | null> => {
    const { promise, resolve } = Promise.withResolvers<Uint8Array | null>();
    matchLogWaiters.push(resolve);
    post({ type: 'matchLog' });
    return promise;
  };
  Object.assign(window, {
    __wmdLobby: (cmd: LobbyCmd) => post({ type: 'lobby', cmd }),
    /** The pacing trace since the last call (`takeTrace`). */
    __wmdTraceTake: takeTrace,
    /** The traced match's log (`Sim::match_log` bytes, or a join log), base64. */
    __wmdMatchLog: () => takeMatchLog().then(bytes => (bytes ? base64(bytes) : null)),
  });
  if (!tracing) return;
  // "Save trace" (a corner button, Ctrl+Shift+S): every record traced since the page loaded and
  // the match log, as `wmd-trace.json` in pacing.mjs's trace.json format (`--analyze` reads it,
  // `--log FILE` writes its match log), without DevTools (an open DevTools window debugs the
  // worker's wasm in its baseline tier: several times slower steps).
  const kept = { page: { frames: [] as number[], messages: [] as number[] }, worker: {} as Record<string, number[]> };
  const keep = (c: { page: PageTrace | null; worker: WorkerTrace[] }): void => {
    if (c.page) { kept.page.frames = kept.page.frames.concat(c.page.frames); kept.page.messages = kept.page.messages.concat(c.page.messages); }
    for (const w of c.worker) for (const [k, v] of Object.entries(w)) kept.worker[k] = (kept.worker[k] ?? []).concat(v);
  };
  const saveTrace = async (): Promise<void> => {
    // The worker's batch up to now (it posts one per take while tracing).
    const batch = Promise.withResolvers<void>();
    traceWaiters.push(batch.resolve);
    setTimeout(batch.resolve, 3000);
    keep(takeTrace());
    await batch.promise;
    keep({ page: null, worker: workerTrace });
    workerTrace = [];
    const log = await takeMatchLog();
    const format = log ? new TextDecoder().decode(log.subarray(0, 8)) : null;
    const data = {
      label: `saved ${new Date().toISOString()}`, version: 2, strides: { frames: FRAME_STRIDE }, userAgent: navigator.userAgent,
      cores: navigator.hardwareConcurrency, room: id, simBuild, me: hooks.me ?? null,
      teams: teams.map(t => ({ name: t.name, ai: t.owner.t === 'Ai', mine: t.owner.t === 'Human' && t.owner.player === hooks.me })),
      turns: [], page: kept.page, worker: kept.worker, desyncs: hooks.desyncs, errors: hooks.errors, released: hooks.released,
      matchLog: log ? base64(log) : null, matchLogFormat: format,
      matchLogNote: !log ? 'none: no match booted or restored in this tab since the trace was on'
        : format === 'WMDJOIN1' ? 'join log: the state snapshot this tab restored and every later input; replays only in the sim build that wrote it (simBuild)' : 'from the boot',
    };
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([JSON.stringify(data)], { type: 'application/json' }));
    a.download = 'wmd-trace.json';
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 60_000);
  };
  const button = document.createElement('button');
  button.className = 'save-trace';
  button.textContent = 'Save trace';
  button.title = 'Ctrl+Shift+S: the pacing trace and the match log as wmd-trace.json';
  let saving = false;
  const save = (): void => {
    if (saving) return;
    saving = true;
    button.textContent = 'Saving…';
    saveTrace().catch(e => hooks.errors.push(`save trace: ${String(e)}`)).finally(() => { saving = false; button.textContent = 'Save trace'; });
  };
  button.addEventListener('click', save);
  // Capture phase: the game's key bindings never see the shortcut.
  window.addEventListener('keydown', e => {
    if (e.ctrlKey && e.shiftKey && e.code === 'KeyS') { e.preventDefault(); e.stopImmediatePropagation(); save(); }
  }, { capture: true });
  document.body.appendChild(button);
}

function base64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

async function replay(): Promise<void> {
  app.innerHTML = '';
  await Promise.all([loadManifest(), loadIndex()]);
  const P = new URLSearchParams(location.search);
  let screen: MatchScreen | null = null;
  const post = startWorker(m => {
    if (m.type === 'ready') hooks.ready = true;
    else if (m.type === 'status') hooks.status = m.text;
    else if (m.type === 'error') { hooks.errors.push(m.text); console.error(m.text); }
    else screen?.onWorker(m);
  });
  screen = new MatchScreen(app, post, P.get('theme') ?? 'China');
  screen.setTeams((P.get('teams') ?? 'Alpha:0,Bravo:1').split(',').map(t => { const [name, c] = t.split(':'); return { name, colour: Number(c), mine: false, wins: 0, user: null }; }), false);
  hooks.match = screen;
  hooks.stats = screen.stats;
  screen.start();
  Object.assign(window, {
    __wmdReplay: (snapshot: Uint8Array, o: { fastTo: number; speed?: number; stopAt?: number; history?: boolean }) => {
      post({ type: 'replay', snapshot, fastTo: o.fastTo, speed: o.speed ?? 1, stopAt: o.stopAt ?? Infinity, history: o.history ?? true });
    },
    __wmdPause: (paused: boolean) => post({ type: 'pause', paused }),
  });
}

const path = location.pathname;
const roomMatch = /^\/m\/([0-9A-Za-z-]+)\/?$/.exec(path);
if (new URLSearchParams(location.search).has('replay')) void replay();
else if (roomMatch) void room(roomMatch[1]);
else home();
